import { EPlacementType } from '@odc-shared/types/medium.type';
import { type ClassValue, clsx } from 'clsx';
import { camelCase, isNil, isEmpty as lodashIsEmpty } from 'lodash';
import { twMerge } from 'tailwind-merge';

import { OSEnum } from '../types/common.type';
/**
 * tailwind css class name 合并
 * @param inputs
 * @returns
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * 将字符串转换为pascal命名法
 * @param str 输入字符串
 * @returns 转换后的字符串
 */
export function pascalCase(str: string): string {
  const res = camelCase(str);
  return res.charAt(0).toUpperCase() + res.slice(1);
}

/**
 * 将字符串转换为下划线命名法
 * @param str 输入字符串
 * @returns 转换后的字符串
 */
export function underScoreCase(str: string): string {
  const res = camelCase(str);
  return res.replace(/([A-Z])/g, '_$1').toLowerCase();
}

/**
 * 深度转换驼峰式属性名为下划线格式
 * @param obj Object to convert
 */
export function camel2UnderScore(obj: any): any {
  const visited = new WeakMap();
  const convert = (_obj: any): any => {
    if (visited.has(_obj)) {
      return visited.get(_obj);
    }
    if (Array.isArray(_obj)) {
      const ret = _obj.map((item) => convert(item));
      visited.set(_obj, ret);
      return ret;
    }
    if (!isNil(_obj) && _obj.constructor === Object) {
      const ret = Object.keys(_obj).reduce((result, key) => {
        const underScoreKey = underScoreCase(key);
        result[underScoreKey] = convert(_obj[key]);
        return result;
      }, {} as any);
      visited.set(_obj, ret);
      return ret;
    }
    return _obj;
  };
  return convert(obj);
}

/**
 * 深度转换下划线属性名为驼峰式
 * @param obj Object to convert
 */
export function underScore2Camel(obj: any): any {
  const visited = new WeakMap();
  const convert = (obj: any): any => {
    if (visited.has(obj)) {
      return visited.get(obj);
    }
    if (Array.isArray(obj)) {
      const ret = obj.map(item => convert(item));
      visited.set(obj, ret);
      return ret;
    }
    if (!isNil(obj) && obj.constructor === Object) {
      const ret = Object.keys(obj).reduce((result, key) => {
        const camelKey = camelCase(key);
        result[camelKey] = convert(obj[key]);
        return result;
      }, {} as any);
      visited.set(obj, ret);

      return ret;
    }
    return obj;
  };
  return convert(obj);
}

/**
 * 锁定和解锁页面滚动
 */
export const lockPageScroll = (lock: boolean) => {
  const lockScrollClass = 'odc-lock-scroll';
  if (lock) {
    document.querySelector('html')?.classList.add(lockScrollClass);
  } else {
    document.querySelector('html')?.classList.remove(lockScrollClass);
  }
};

/**
 * 计算根据外层圆环半径和角度位置，计算小球位置
 * @param {number} cx 圆心x
 * @param {number} cy 圆心y
 * @param {number} radius 圆环半径
 * @param {number} angle 角度位置
 * @returns {object} 小球位置
 */
export const getBallPosition = (cx = 960, cy = 960, radius = 960, angle = 300) => {
  const centerX = cx;
  const centerY = cy;
  const radian = (angle * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(radian),
    y: centerY + radius * Math.sin(radian),
  };
};

/**
 * 检验传入的内容是否为空
 * @param value 需要检验的值
 * @returns {boolean} true: 为空，false: 不为空
 */
export const isEmpty = (value: any): boolean => {
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  if (typeof value === 'number') {
    return false;
  }
  return lodashIsEmpty(value);
};

export function getPlacementTypeText(placementType?: string) {
  if (!placementType) return '';
  const text = ({
    [EPlacementType.Rewarded]: 'Rewarded',
    [EPlacementType.Interstitial]: 'Interstitial',
    [EPlacementType.Banner]: 'Banner',
    [EPlacementType.AppOpen]: 'App open',
  } as any)[placementType] || placementType;
  return text;
}

export function getPlatformText(platform: OSEnum | string) {
  const textMap: Record<OSEnum | string, string> = {
    [OSEnum.Android]: 'Android',
    [OSEnum.iOS]: 'iOS',
    [OSEnum.Web]: 'Web',
    [OSEnum.Unknown]: 'Unknown',
  };
  return textMap[platform] || platform;
};

/**
 * 检验传入的URL是否合法
 * @param url 需要检验的值
 * @returns {boolean} true: 合法，false: 不合法
 *
 * @description 支持http、https协议，支持www子域名，支持ip地址，支持端口号，支持路径和查询参数
 */
export function checkURL(url: string): boolean {
  if (!url) return true;
  const reg = /^(http|https):(?:\/\/(?:www\.)?[a-zA-Z0-9-][a-zA-Z0-9.-]*[a-zA-Z0-9]|\/\/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::\d+)?(?:\/[^\s?#]*)?(?:\?[^\s#]*)?$/i;
  return reg.test(url);
}

/**
 * 检验传入的name是否包含特殊字符
 * @param name 需要检验的值
 * @returns {boolean} true: 不包含，false: 包含
 */
export function checkName(name: string): boolean {
  // eslint-disable-next-line no-control-regex
  const reg = /^[^\<\>\"\x09\x0A\x0D\\\/]*$/;
  return reg.test(name);
}

export function checkEmail(email: string): boolean {
  if (!email) return true;
  const emailRegex = /^(?!\.)(?!.*\.\.)([a-z0-9_'+\-\.]*)[a-z0-9_+-]@([a-z0-9][a-z0-9\-]*\.)+[a-z]{2,}$/i;
  return emailRegex.test(email);
}

/**
 *  复制文本到剪贴板
 * @param text 需要复制的文本
 * @returns
 */
export function copyText(text: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  const result = document.execCommand('copy');
  document.body.removeChild(textarea);
  return result;
}

/**
 *  检测设备id是否合法
 * @param id 待检测的id
 * @param os {ios | android} 设备操作系统
 * @returns {boolean} true: 合法，false: 不合法
 */
export function checkDeviceId(id: string, os: 'ios' | 'android'): boolean {
  let reg = /^[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}$/;
  if (os === 'ios') {
    reg = /^[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12}$/;
  }
  return reg.test(id);
}